import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Folder } from 'lucide-react';
import * as React from 'react';
import { ARTICLES_PAGE_LIMIT } from '../KnowledgeManager.page';
import { DataTablePagination } from './data-table-pagination';
import { DataTableToolbar } from './data-table-toolbar';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import { cn } from '@/Domain/utils/cn';

type OpenDropdownRowContextValue = {
  openRowId: string | null;
  setOpenRowId: (id: string | null) => void;
};

const OpenDropdownRowContext = React.createContext<OpenDropdownRowContextValue>({
  openRowId: null,
  setOpenRowId: () => {},
});

export const useOpenDropdownRow = () => React.useContext(OpenDropdownRowContext);

type Props<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  onSingleParagraphClick?: () => void;
  selectionToolbar?: React.ReactNode;
  emptyState?: React.ReactNode;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (pageIndex: number) => void;
  onColumnFiltersChangeExternal?: (filters: ColumnFiltersState) => void;
  onGlobalFilterChangeExternal?: (value: string) => void;
  onRefreshAll?: () => void;
  refreshAllDisabled?: boolean;
};

type ColumnMetaConfig = {
  headerClassName?: string;
  cellClassName?: string;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  isStickyEdge?: boolean;
  stickyPosition?: 'left' | 'right';
  stickyWidth?: number;
};

export const DataTable = <TData, TValue>({
  columns,
  data,
  onRowClick,
  rowSelection: externalRowSelection,
  onRowSelectionChange,
  onSingleParagraphClick,
  selectionToolbar,
  emptyState,
  totalItems = 0,
  pageSize = ARTICLES_PAGE_LIMIT,
  onPageChange,
  onColumnFiltersChangeExternal,
  onGlobalFilterChangeExternal,
  onRefreshAll,
  refreshAllDisabled,
}: Props<TData, TValue>) => {
  const [internalRowSelection, setInternalRowSelection] = React.useState({});
  const rowSelection = externalRowSelection ?? internalRowSelection;
  const [openRowId, setOpenRowId] = React.useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const rootRef = React.useRef<HTMLDivElement>(null);
  const scrollElementRef = React.useRef<HTMLDivElement | null>(null);
  const [showLeftShadow, setShowLeftShadow] = React.useState(false);
  const [showRightShadow, setShowRightShadow] = React.useState(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');

  const pageCount = React.useMemo(() => {
    if (typeof totalItems !== 'number' || totalItems < 0) return -1;
    return Math.ceil(totalItems / pagination.pageSize);
  }, [totalItems, pagination.pageSize]);

  const stickyWidths = React.useMemo(() => {
    const leftSegments: number[] = [];
    const rightSegments: number[] = [];

    const accumulate = (defs: ColumnDef<TData, TValue>[]) => {
      defs.forEach((def) => {
        const meta = def.meta as ColumnMetaConfig | undefined;
        if (meta?.isStickyEdge) {
          const width = meta.stickyWidth ?? 0;
          if (meta.stickyPosition === 'left') {
            leftSegments.push(width);
          } else if (meta.stickyPosition === 'right') {
            rightSegments.push(width);
          }
        }

        if ('columns' in def && Array.isArray(def.columns)) {
          accumulate(def.columns as ColumnDef<TData, TValue>[]);
        }
      });
    };

    accumulate(columns);

    return {
      leftSegments,
      rightSegments,
      totalLeft: leftSegments.reduce((sum, value) => sum + value, 0),
      totalRight: rightSegments.reduce((sum, value) => sum + value, 0),
    };
  }, [columns]);

  const updateShadowState = React.useCallback(() => {
    const container = scrollElementRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const showThreshold = 6;
    const hideThreshold = 3;
    const hasHorizontalOverflow = maxScrollLeft > hideThreshold;

    setHasOverflow(hasHorizontalOverflow);
    if (!hasHorizontalOverflow) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    const effectiveLeftScroll = Math.max(0, scrollLeft);
    const distanceFromEnd = Math.max(0, maxScrollLeft - scrollLeft);
    const effectiveRightDistance = Math.max(0, distanceFromEnd - stickyWidths.totalRight);

    setShowLeftShadow((prev) => {
      if (prev) {
        return effectiveLeftScroll > hideThreshold;
      }
      return effectiveLeftScroll > showThreshold;
    });

    setShowRightShadow((prev) => {
      if (prev) {
        return effectiveRightDistance > hideThreshold;
      }
      return effectiveRightDistance > showThreshold;
    });
  }, [stickyWidths]);

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
      pagination,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    manualPagination: true,
    manualFiltering: true,
    enableSorting: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    pageCount,
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      if (onRowSelectionChange) {
        onRowSelectionChange(newSelection);
      } else {
        setInternalRowSelection(newSelection);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: (updater) => {
      setColumnFilters((old) => {
        const next = typeof updater === 'function' ? updater(old) : updater;
        onColumnFiltersChangeExternal?.(next);

        setPagination((prev) => {
          if (prev.pageIndex === 0) return prev;
          const updated = { ...prev, pageIndex: 0 };
          onPageChange?.(0);
          return updated;
        });

        return next;
      });
    },
    onGlobalFilterChange: (updater) => {
      setGlobalFilter((old) => {
        const next = typeof updater === 'function' ? updater(old) : updater;
        onGlobalFilterChangeExternal?.(next);

        setPagination((prev) => {
          if (prev.pageIndex === 0) return prev;
          const updated = { ...prev, pageIndex: 0 };
          onPageChange?.(0);
          return updated;
        });

        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((old) => {
        const next = typeof updater === 'function' ? updater(old) : updater;

        if (next.pageIndex !== old.pageIndex) {
          onPageChange?.(next.pageIndex);
        }

        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row: any) => row.id,
    autoResetPageIndex: false,
  });

  const hasDataRows = table.getRowModel().rows?.length > 0;

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const scrollElement = root.querySelector<HTMLDivElement>('[data-table-scroll-container]');
    if (!scrollElement) return;

    scrollElementRef.current = scrollElement;

    const handleScroll = () => updateShadowState();

    const resizeObserver = new ResizeObserver(() => updateShadowState());
    const mutationObserver = new MutationObserver(() => updateShadowState());

    updateShadowState();
    scrollElement.addEventListener('scroll', handleScroll);
    resizeObserver.observe(scrollElement);
    mutationObserver.observe(scrollElement, { childList: true, subtree: true });

    const handleWindowResize = () => updateShadowState();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      if (scrollElementRef.current === scrollElement) {
        scrollElementRef.current = null;
      }
    };
  }, [data, columns, updateShadowState]);

  return (
    <OpenDropdownRowContext.Provider value={{ openRowId, setOpenRowId }}>
      <div className="relative space-y-4">
        <DataTableToolbar
          table={table}
          onRefreshAll={onRefreshAll}
          refreshAllDisabled={refreshAllDisabled}
        />

        <div ref={rootRef} className="relative w-full custom-scroll">
          <Table className="min-w-full">
            <TableHeader className="sticky top-0 z-20 bg-accent">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnMeta = header.column.columnDef.meta as ColumnMetaConfig | undefined;

                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        className={cn(
                          'relative cursor-default select-none bg-accent text-sm font-medium text-muted-foreground',
                          columnMeta?.headerClassName,
                        )}
                        style={columnMeta?.headerStyle}
                        data-sticky={
                          columnMeta?.isStickyEdge ? columnMeta?.stickyPosition : undefined
                        }
                      >
                        {header.isPlaceholder ? null : (
                          <span className="block leading-tight whitespace-nowrap">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {/* Single Paragraphs Row */}
              {onSingleParagraphClick && (
                <TableRow
                  className={cn(
                    'h-[49px]',
                    // No dropdown owner possible on this synthetic row, so any
                    // open dropdown is always "other" — mirror the regular-row
                    // suppression: drop hover bg, pointer cursor, and the
                    // sticky cells' group-hover paint.
                    openRowId === null
                      ? 'cursor-pointer hover:!bg-accent'
                      : '!cursor-default hover:!bg-card',
                  )}
                  onClick={() => {
                    if (openRowId !== null) return;
                    onSingleParagraphClick();
                  }}
                >
                  <TableCell
                    className={cn(
                      'sticky left-0 z-30 w-12 min-w-12 max-w-12 bg-card px-4 transition-colors group-hover:!bg-accent',
                      openRowId !== null && 'group-hover:!bg-card',
                    )}
                    style={{ left: 0 }}
                  >
                    <Folder className="h-4 w-4" />
                  </TableCell>
                  <TableCell
                    className={cn(
                      'sticky left-[56px] z-20 w-[320px] min-w-[320px] max-w-[320px] bg-card transition-colors group-hover:!bg-accent',
                      openRowId !== null && 'group-hover:!bg-card',
                    )}
                    style={{ left: 56 }}
                  >
                    <span className="text-sm font-semibold text-foreground">Single Paragraphs</span>
                  </TableCell>
                  <TableCell colSpan={Math.max(columns.length - 3, 1)}>
                    <Badge variant="secondary" className="text-xs">
                      Paragraph
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'sticky right-0 z-30 w-12 min-w-12 max-w-12 bg-card transition-colors group-hover:!bg-accent',
                      openRowId !== null && 'group-hover:!bg-card',
                    )}
                    style={{ right: 0 }}
                  />
                </TableRow>
              )}

              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const isMyDropdownOpen = openRowId === row.id;
                  const isOtherDropdownOpen = openRowId !== null && !isMyDropdownOpen;
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn(
                        onRowClick && !isOtherDropdownOpen && 'cursor-pointer',
                        // Suppress hover bg + pointer cursor on other rows while a
                        // dropdown is open, so the active row remains visually
                        // distinct and stray cursor feedback doesn't suggest a
                        // click target.
                        isOtherDropdownOpen && '!cursor-default hover:!bg-card',
                        // Force-apply the hover background on the row whose
                        // dropdown is open, regardless of pointer position.
                        isMyDropdownOpen && '!bg-accent',
                      )}
                      onClick={() => {
                        if (openRowId !== null) return;
                        if (onRowClick) {
                          onRowClick(row.original);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const columnMeta = cell.column.columnDef.meta as
                          | ColumnMetaConfig
                          | undefined;
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              columnMeta?.cellClassName,
                              // Sticky cells carry their own `group-hover:!bg-accent`
                              // that paints over the row's background. Override
                              // those rules when another row's dropdown is open
                              // (force back to card) and when this row owns the
                              // open dropdown (force-keep the highlight).
                              isOtherDropdownOpen && 'group-hover:!bg-card',
                              isMyDropdownOpen && '!bg-accent',
                            )}
                            style={columnMeta?.cellStyle}
                            data-sticky={
                              columnMeta?.isStickyEdge ? columnMeta?.stickyPosition : undefined
                            }
                            onClick={(e) => {
                              // Prevent row click when interacting with checkboxes or action buttons
                              if (cell.column.id === 'select' || cell.column.id === 'actions') {
                                e.stopPropagation();
                              }
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length || columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {emptyState ?? 'No results'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {hasOverflow &&
            hasDataRows &&
            stickyWidths.totalLeft > (stickyWidths.leftSegments[0] ?? 0) && (
              <div
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-y-0 z-20 w-10 bg-gradient-to-r from-background/70 via-background/0 to-transparent transition-opacity duration-200',
                  showLeftShadow ? 'opacity-100' : 'opacity-0',
                )}
                style={{ left: `${stickyWidths.totalLeft}px` }}
              />
            )}
          {hasOverflow && hasDataRows && (
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-y-0 z-20 w-8 bg-gradient-to-l from-background/70 via-background/0 to-transparent transition-opacity duration-200',
                showRightShadow ? 'opacity-100' : 'opacity-0',
              )}
              style={{ right: stickyWidths.totalRight ? `${stickyWidths.totalRight}px` : 0 }}
            />
          )}
        </div>
        <DataTablePagination table={table} />
        {selectionToolbar && (
          <div className="pointer-events-none sticky bottom-8 z-30 flex justify-center">
            <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-2.5 shadow-[0_8px_40px_rgba(0,0,0,0.18)]">
              {selectionToolbar}
            </div>
          </div>
        )}
      </div>
    </OpenDropdownRowContext.Provider>
  );
};
